import { createQueryBuilderV4 } from "@odata2ts/odata-query-builder";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { odataFetch } from "../../lib/odata-fetch";
import {
  UADMCompleteWOOperationSerializedList2Params,
  UADMCompleteWOOperationSerializedListResponse,
} from "../../odata/appu4dm/AppU4DMModel";
import { QUADMCompleteWOOperationSerializedList } from "../../odata/appu4dm/QAppU4DM";
import { fetchConfiguration } from "../configuration/use-configuration";
import { useMachine } from "../machine/use-machine";
import { mtuKeys } from "../mtu/mtu-keys";
import { taskKeys } from "../tasks/task-keys";
import { useWorkOrder } from "../work-order/use-work-order";
import { useWorkOrderOperation } from "../work-order/use-work-order-operation";
import { useWorkOrderOperationId } from "../work-order/use-work-order-operation-id";
import { workOrderKeys } from "../work-order/work-order-keys";

export function useCompleteOperation(props?: {
  onSuccess?: VoidFunction;
  onError?: (e: Error) => void;
}) {
  const queryClient = useQueryClient();
  const { machine } = useMachine();
  const { workOrderOperationId } = useWorkOrderOperationId();
  const { workOrderOperation } = useWorkOrderOperation();
  const { workOrder } = useWorkOrder();
  const { token } = queryClient.getDefaultOptions().queries?.meta ?? {};

  const { mutate, ...mutation } = useMutation({
    mutationFn: (items: MTU[]) =>
      completeOperation(
        {
          workOrderOperationId: workOrderOperationId!.WorkOrderOperation_Id!,
          mtus: items,
        },
        token!
      ),
    onSuccess: async () =>
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: workOrderKeys.getOperationId(machine?.EquipmentNId),
        }),
        queryClient.invalidateQueries({
          queryKey: mtuKeys.get(workOrderOperationId?.WorkOrderOperation_Id),
        }),
        queryClient.invalidateQueries({
          queryKey: taskKeys.listAll(
            machine?.EquipmentNId,
            workOrderOperation?.NId,
            workOrder?.NId,
            workOrder?.FinalMaterial.Material.UId
          ),
        }),
        props?.onSuccess?.(),
      ]),
    onError: props?.onError,
  });

  return {
    completeOperation: mutate,
    ...mutation,
  };
}

type MTU = {
  id: string;
  nId: string;
  value: string;
};

type Payload = {
  workOrderOperationId: string;
  mtus: MTU[];
};

async function completeOperation(
  { workOrderOperationId, mtus }: Payload,
  token: string
) {
  const builder = createQueryBuilderV4(
    "UADMCompleteWOOperationSerializedList",
    new QUADMCompleteWOOperationSerializedList()
  );

  const config = await fetchConfiguration("ResultStrategyManagement", token);

  return odataFetch<
    UADMCompleteWOOperationSerializedListResponse,
    UADMCompleteWOOperationSerializedList2Params
  >({
    builder,
    token,
    method: "POST",
    body: {
      command: {
        CompleteSerializedWoOpParameterList: [
          {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ElectronicSignatureId: undefined as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            AvoidPausingOtherSerialNos: undefined as any,
            WorkOrderOperationId: workOrderOperationId,
            DM_MTUIdList: mtus.map((x) => x.id),
            Results: mtus.map((x) => ({
              MaterialTrackingUnitNId: x.nId,
              ResultValue: x.value,
              Strategy: config?.value[0].Val ?? "",
            })),
          },
        ],
      },
    },
  });
}
